import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Loader,
  LoadingOverlay,
  Select,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconActivity,
  IconEdit,
  IconFileExport,
  IconFunction,
  IconHandStop,
  IconLock,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import * as React from "react";
import * as xlsx from "xlsx";
import { useEffect, useState } from "react";
import "../../Styles/tab.component.css";
import { repositoryMaster, repositoryPos } from "../../_base/_const/_constVar";
import _breadcrumb from "../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { formatDateTransfer } from "../../common/FormatDate/FormatDate";
import { _localization } from "../../config/location";
import { MessageResponse } from "../../model/MessageResponse";
import {
  EmployeeShortInfo,
  UserMasterModel,
} from "../../model/UserMasterModel";
import UserAddGroup from "./UserAddGroup";
import UserCreate from "./UserCreate";
import UserEdit from "./UserEdit";

const UserListView = () => {
  //#region  table
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<UserMasterModel[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  //table state
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [keySearch, setKeySearch] = useDebouncedState("", 200);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [isActiveFilter, setIsActiveFilter] = useState<string | null>(null);

  const isActiveFilterRef = React.useRef(isActiveFilter);
  isActiveFilterRef.current = isActiveFilter;

  const keySearchRef = React.useRef(keySearch);
  keySearchRef.current = keySearch;

  useEffect(() => {
    const valuesList = Object.keys(rowSelection);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelection);
      setSelectIds(valuesList);
    }
  }, [rowSelection]);

  useEffect(() => {
    fetchData();
  }, [sorting, pagination.pageIndex, pagination.pageSize, isActiveFilter]);

  /**
   * Phòng ban và chức vụ nằm ở database POS, tài khoản nằm ở database master.
   * Master chỉ trả về mã nhân viên, nên phải hỏi thêm sang POS rồi ghép vào theo mã.
   *
   * Hỏi một lượt cho cả trang thay vì hỏi từng dòng, và hỏng thì bỏ qua:
   * thiếu phòng ban chỉ là thiếu thông tin phụ, không đáng để hỏng cả màn hình.
   */
  /**
   * Lấy thông tin nhân viên theo đúng danh sách mã đang cần.
   * Dùng cho lưới, mỗi trang chỉ vài chục dòng nên đường dẫn vẫn ngắn.
   */
  const fetchEmployeeMapByIds = async (users: UserMasterModel[]) => {
    const ids = Array.from(
      new Set(users.map((u) => u.empid).filter((id): id is number => !!id)),
    );

    const byId = new Map<number, EmployeeShortInfo>();
    if (ids.length === 0) return byId;

    const res = await repositoryPos.get<MessageResponse<EmployeeShortInfo[]>>(
      `/api/v1/TblDmEmployee/get-info-by-ids?ids=${ids.join(",")}`,
    );

    if (res && res.success && res.data) {
      res.data.forEach((e) => byId.set(e.id, e));
    }

    return byId;
  };

  /**
   * Lấy toàn bộ nhân viên trong một lần gọi, dùng cho bản xuất file.
   *
   * Bản xuất có hàng nghìn dòng, nếu truyền từng ấy mã nhân viên lên đường dẫn
   * sẽ tạo ra một URL dài vài nghìn ký tự, dễ bị máy chủ hoặc proxy chặn.
   */
  const fetchAllEmployeeMap = async () => {
    const byId = new Map<number, EmployeeShortInfo>();

    const res = await repositoryPos.get<MessageResponse<EmployeeShortInfo[]>>(
      "/api/v1/TblDmEmployee/get-all-info",
    );

    if (res && res.success && res.data) {
      res.data.forEach((e) => byId.set(e.id, e));
    }

    return byId;
  };

  const fetchEmployeeInfo = async (users: UserMasterModel[]) => {
    try {
      const byId = await fetchEmployeeMapByIds(users);
      if (byId.size === 0) return;

      setData((prev) =>
        prev.map((u) => {
          const emp = u.empid ? byId.get(u.empid) : undefined;
          if (!emp) return u;
          return {
            ...u,
            empCode: emp.code,
            depName: emp.depName,
            roleName: emp.roleName,
            managerName: emp.managerName,
          };
        }),
      );
    } catch (error) {
      console.error("Không lấy được thông tin nhân viên từ POS", error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    try {
      let urlGet =
        "/api/v1/auth/get-list?Skip=" +
        pagination.pageIndex * pagination.pageSize +
        "&Take=" +
        pagination.pageSize;

      if (keySearch && keySearch.length > 0)
        urlGet = urlGet + "&KeySearch=" + encodeURIComponent(keySearch);

      if (isActiveFilter !== null && isActiveFilter !== "") {
        urlGet =
          urlGet + "&IsActive=" + isActiveFilter + "&Active=" + isActiveFilter;
      }

      let _softing = "";
      let isOrder = false;
      if (sorting && sorting.length > 0) {
        let _q = sorting[0];
        if (_q) {
          _softing = _q.id;
          isOrder = _q.desc;
        }
      }
      urlGet = urlGet + "&Sort=" + _softing + "&IsOrder=" + isOrder;

      const response =
        await repositoryMaster.get<MessageResponse<UserMasterModel[]>>(urlGet);
      if (response && response.success) {
        const users = response.data ?? [];

        // Hiển thị danh sách ngay, không chờ POS trả lời.
        // Phòng ban và chức vụ là thông tin phụ, không đáng để giữ cả bảng lại chờ.
        setData(users);
        setRowCount(response.totalCount);
        setSelectIds([]);
        table.resetRowSelection();

        void fetchEmployeeInfo(users);
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  const activeData = async (status: boolean) => {
    if (selectIds.length < 1)
      NotificationExtension.Success("Bạn chưa chọn dữ liệu !");
    else
      try {
        const res = await repositoryMaster.post<MessageResponse<boolean>>(
          "/api/v1/auth/actives",
          {
            ids: selectIds,
            status: status,
          },
        );
        console.log(res);
        if (res && res.success) {
          NotificationExtension.Success("Cập nhật thành công !");
          await fetchData();
        } else NotificationExtension.Fails("Cập nhật thất bại !");
      } catch (error) {
        console.error(error);
        return;
      }
  };
  const activesData = async (status: boolean, id: string) => {
    if (!id || id.length < 1)
      NotificationExtension.Success("Bạn chưa chọn dữ liệu !");
    else
      try {
        const res = await repositoryMaster.post<MessageResponse<boolean>>(
          "/api/v1/auth/actives",
          {
            ids: [id],
            status: status,
          },
        );
        if (res && res.success) {
          await fetchData();
          NotificationExtension.Success("Cập nhật thành công !");
        } else NotificationExtension.Fails("Cập nhật thất bại !");
      } catch (error) {
        console.error(error);
        return;
      }
  };
  const deletesData = async (ids: string[]) => {
    if (ids.length < 1)
      NotificationExtension.Success("Bạn chưa chọn dữ liệu !");
    else
      try {
        modals.openConfirmModal({
          title: (
            <>
              <Title order={5}>Bạn có thực sự muốn xóa!</Title>
            </>
          ),

          size: "400px",
          // children: <>Bạn có thực sự muốn xóa ?</>,
          labels: { confirm: "Xác nhận", cancel: "Hủy bỏ" },
          onConfirm: async () => {
            const res = await repositoryMaster.post<MessageResponse<boolean>>(
              "/api/v1/auth/delete",
              ids,
            );
            if (res)
              if (res.success) {
                await fetchData();
                NotificationExtension.Success("Xóa thành công !");
              } else NotificationExtension.Fails("Xóa thất bại !");
          },
          classNames: {
            title: "custom-modal-title", // Class cho title
            body: "custom-modal-body", // Class cho nội dung
          },
        });
      } catch (error) {
        console.error(error);
        return;
      }
  };
  /**
   * Xuất Excel ngay tại trình duyệt.
   *
   * Trước đây gọi API export-csv của server, nhưng server dựng chuỗi XML kiểu cũ
   * rồi đặt tên đuôi .xlsx nên Excel mở lên luôn cảnh báo sai định dạng.
   * Dựng file ở đây bằng thư viện xlsx cho ra file đúng chuẩn,
   * và cũng ghép được phòng ban với chức vụ giống hệt bảng đang hiển thị.
   */
  const handleExportExcel = async () => {
    setIsRefetching(true);
    try {
      // Lấy toàn bộ người dùng theo đúng điều kiện lọc đang chọn, không chỉ trang hiện tại
      let url = "/api/v1/auth/get-list?Skip=0&Take=100000";
      const currentKeySearch = keySearchRef.current;
      const currentIsActive = isActiveFilterRef.current;

      if (currentKeySearch && currentKeySearch.length > 0) {
        url += "&KeySearch=" + encodeURIComponent(currentKeySearch);
      }
      if (currentIsActive !== null && currentIsActive !== "") {
        url += `&IsActive=${currentIsActive}&Active=${currentIsActive}`;
      }

      let _sorting = "";
      let isOrder = false;
      if (sorting && sorting.length > 0) {
        const _q = sorting[0];
        if (_q) {
          _sorting = _q.id;
          isOrder = _q.desc;
        }
      }
      url += "&Sort=" + _sorting + "&IsOrder=" + isOrder;

      const response =
        await repositoryMaster.get<MessageResponse<UserMasterModel[]>>(url);

      if (!response || !response.success) {
        NotificationExtension.Fails("Export Excel thất bại !");
        return;
      }

      const users = response.data ?? [];
      const byId = await fetchAllEmployeeMap();

      const rows = users.map((u, index) => {
        const emp = u.empid ? byId.get(u.empid) : undefined;
        return {
          STT: index + 1,
          "Tên đăng nhập": u.userName ?? "",
          "Họ và tên": u.fullName ?? "",
          "Mã nhân viên": emp?.code ?? "",
          "Phòng ban": emp?.depName ?? "",
          "Chức vụ": emp?.roleName ?? "",
          "Người quản lý trực tiếp": emp?.managerName ?? "",
          "Số điện thoại": u.phone ?? "",
          "Người tạo": u.createBy ?? "",
          "Ngày tạo": u.createDate ?? "",
          "Trạng thái": u.inActive ? "Hoạt động" : "Không hoạt động",
          "Số lần nhập sai": u.numberinputpassfails ?? 0,
        };
      });

      const worksheet = xlsx.utils.json_to_sheet(rows);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Danh sách người dùng");

      const _name = new Date().toISOString().slice(0, 10);
      xlsx.writeFile(workbook, `danh_sach_nguoi_dung_${_name}.xlsx`);

      NotificationExtension.Success("Export Excel thành công !");
    } catch (error) {
      console.error("Export Excel error:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi xuất Excel !");
    } finally {
      setIsRefetching(false);
    }
  };


  function createItem() {
    modals.openConfirmModal({
      title: (
        <>
          <Title order={5}>Thêm mới người dùng !</Title>
        </>
      ),

      size: "550px",
      children: <UserCreate onLoad={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  }

  function editItem(id: string) {
    modals.openConfirmModal({
      title: (
        <>
          <Title order={5}>Chỉnh sửa !</Title>
        </>
      ),

      size: "550px",
      children: <UserEdit id={id} onLoad={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  }

  const groupAddRoleModal = (idAuthozire: string) => {
    modals.closeAll();

    modals.openConfirmModal({
      title: "Danh sách nhóm",

      size: "100%",
      fullScreen: false,
      children: <UserAddGroup idAuthozire={idAuthozire}></UserAddGroup>,
      labels: { confirm: "Confirm", cancel: "Hủy bỏ" },
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  function getColorStatus(text: boolean) {
    switch (text) {
      case true:
        return "teal";
      default:
        return "red";
    }
  }

  const columns = React.useMemo<MRT_ColumnDef<UserMasterModel>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
      },
      {
        accessorKey: "userName",
        header: "Tên đăng nhập",
      },
      {
        accessorKey: "fullName",
        header: "Tên đầy đủ",
      },
      {
        accessorKey: "empCode",
        header: "Mã nhân viên",
      },
      {
        accessorKey: "depName",
        header: "Phòng ban",
      },
      {
        accessorKey: "roleName",
        header: "Chức vụ",
      },
      {
        accessorKey: "managerName",
        header: "Người quản lý trực tiếp",
      },
      {
        accessorKey: "phone",
        header: "Số điện thoại",
      },
      {
        accessorKey: "inActive",
        header: "Trạng thái",
        Cell: ({ renderedCellValue, row }) => (
          <>
            <Badge color={getColorStatus(row.original.inActive ?? false)}>
              {row.original.inActive ? "Đang hoạt động" : "Dừng hoạt động"}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "codeActive",
        header: "Mã kích hoạt",
      },
      {
        accessorKey: "numberinputpassfails",
        header: "Số lần nhập sai",
      },
      {
        accessorKey: "locktime",
        header: "Ngày khóa",
        Cell: ({ renderedCellValue }: any) => {
          return <>{formatDateTransfer(renderedCellValue)}</>;
        },
      },
      {
        accessorKey: "createBy",
        header: "Người tạo",
      },
      {
        accessorKey: "createDate",
        header: "Ngày tạo",
      },
      {
        accessorKey: "action",
        header: "Phân quyền và thao tác",
        Cell: ({ renderedCellValue, row, table }) => (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Tooltip label="Nhóm">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color="cyan"
                onClick={() => {
                  groupAddRoleModal(row.original.id.toString());
                }}
              >
                <IconFunction size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Mỏ khóa">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color=""
                onClick={async () => {
                  try {
                    const res = await repositoryMaster.get<
                      MessageResponse<boolean>
                    >(`/api/v1/auth/un-lock?id=${row.original.id}`);
                    if (res && res.httpStatusCode === 200) {
                      if (res.success) {
                        await fetchData();
                        NotificationExtension.Success("Mở khóa thành công !");
                      } else NotificationExtension.Fails("Mở khóa thất bại !");
                    }
                  } catch (error) {
                    console.error(error);
                    NotificationExtension.Fails("Mở khóa thất bại !");
                  }
                }}
              >
                <IconLock size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color="orange"
                onClick={async () => {
                  editItem(row.original.userName);
                }}
              >
                <IconEdit size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>

            <Tooltip
              label={
                row.original.inActive === true ? "Dừng hoạt động" : "Hoạt động"
              }
            >
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color={row.original.inActive === true ? "violet" : "green"}
                onClick={async () => {
                  await activesData(
                    !row.original.inActive,
                    row.original.id.toString(),
                  );
                }}
              >
                {row.original.inActive === true ? (
                  <IconHandStop size={20} />
                ) : (
                  <IconActivity size={20} />
                )}
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Xóa">
              <ActionIcon
                variant="light"
                aria-label="Settings"
                color="red"
                onClick={async () => {
                  await deletesData([row.original.id.toString()]);
                }}
              >
                <IconTrash size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (200 + headerHeight));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const table = useMantineReactTable({
    columns,
    data,
    positionToolbarAlertBanner: "bottom",
    renderTopToolbarCustomActions: ({ table }) => (
      <div ref={headerRef}>
        <_breadcrumb></_breadcrumb>
        <Flex
          gap="md"
          justify="flex-start"
          align="center"
          direction="row"
          wrap="wrap"
        >
          <Input
            placeholder="Nhập từ khóa ..."
            onChange={(e) => {
              //const k = e.target.value ?? "";
              setKeySearch(e.target.value);
            }}
          />

          <Select
            placeholder="Trạng thái"
            data={[
              { value: "", label: "Tất cả trạng thái" },
              { value: "true", label: "Đang hoạt động" },
              { value: "false", label: "Dừng hoạt động" },
            ]}
            value={isActiveFilter}
            onChange={(value) => setIsActiveFilter(value)}
            style={{ width: 180 }}
            clearable
          />

          <Button
            leftSection={<IconSearch size={14} />}
            color="blue"
            variant="outline"
            onClick={async () => {
              await fetchData();
            }}
          >
            Tìm kiếm
          </Button>
        </Flex>
      </div>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Flex
          gap="md"
          justify={{ sm: "flex-start", lg: "flex-end" }}
          align="center"
          direction="row"
          wrap="wrap"
        >
          <Button
            leftSection={<IconPlus size={14} />}
            color="blue"
            variant="outline"
            onClick={async () => {
              createItem();
            }}
          >
            Thêm mới
          </Button>

          <Button
            leftSection={<IconFileExport size={14} />}
            color="green"
            variant="outline"
            onClick={handleExportExcel}
          >
            Export Excel
          </Button>
          <Button
            leftSection={<IconActivity size={14} />}
            color="green"
            variant="outline"
            onClick={async () => {
              await activeData(true);
            }}
            disabled={selectIds.length < 1}
          >
            Hoạt động
          </Button>
          <Button
            leftSection={<IconHandStop size={14} />}
            color="red"
            variant="outline"
            onClick={async () => {
              await activeData(false);
            }}
            disabled={selectIds.length < 1}
          >
            Dừng hoạt động
          </Button>
          <Button
            leftSection={<IconTrash size={14} />}
            color="red"
            variant="outline"
            onClick={async () => {
              await deletesData(selectIds);
            }}
            disabled={selectIds.length < 1}
          >
            Xóa nhóm (đã chọn)
          </Button>
        </Flex>
      </>
    ),

    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    // enableRowNumbers: true,
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row?.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select", "userName"],
        right: ["action"],
      },
      columnVisibility: { id: false },
      density: "xs",
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        //  fontWeight: row.getIsSelected() ? "bold" : "normal", //conditional styling
        fontWeight: "bold", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
        //  backgroundColor: row.original.userName.length>0 ? "white" : "gray",
      },
    }),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      sorting,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "75"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });
  //#endregion
  return (
    <>
      <div className="mt-5">
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <MantineReactTable table={table} />
      </div>
    </>
  );
};
export default UserListView;
